import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useEvents } from "../hooks/useEvents";
import { Event } from "../api/contentful";
import { slugify } from "../utils/slugify";
import "./EventDetailsPage.css";
import { getFormattedDate } from "../components/dashboard/utils/getFormattedDate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faLocationDot } from "@fortawesome/free-solid-svg-icons";
import {
  createEventTicketCheckoutSession,
  EVENT_TICKET_COMMENT_MAX_LENGTH,
} from "../api/eventTickets";
import LoadingSpinner from "../components/LoadingSpinner";
import { linkifyPlainText } from "../utils/linkifyPlainText";

function getTicketsRemaining(event: Event): number | undefined {
  if (event.availableTicketCount === undefined) return undefined;
  const purchased = event.purchasedTicketCount ?? 0;
  const reserved = event.reservedTicketCount ?? 0;
  return Math.max(0, event.availableTicketCount - purchased - reserved);
}

const EventDetailsPage: React.FC = () => {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: events, loading, error, refetch } = useEvents();
  const [event, setEvent] = useState<Event | null>(null);
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [ticketComment, setTicketComment] = useState("");
  const [isRedirectingToPayment, setIsRedirectingToPayment] = useState(false);
  const [ticketError, setTicketError] = useState<string | null>(null);
  const [paymentNotice, setPaymentNotice] = useState<
    "success" | "cancelled" | null
  >(null);

  useEffect(() => {
    const status = searchParams.get("payment");
    if (status !== "success" && status !== "cancelled") return;

    setPaymentNotice(status);
    setSearchParams({}, { replace: true });
    if (status === "success") {
      refetch();
    }
  }, [searchParams, setSearchParams, refetch]);

  useEffect(() => {
    if (events && slug) {
      const matched = events.find((e) => slugify(e.eventName) === slug);
      setEvent(matched || null);
    }
  }, [events, slug]);

  useEffect(() => {
    if (!event) return;

    const availableTickets = getTicketsRemaining(event);
    if (availableTickets !== undefined && ticketQuantity > availableTickets) {
      setTicketQuantity(Math.max(availableTickets, 1));
    }
  }, [event, ticketQuantity]);

  if (loading) {
    return <LoadingSpinner className="loading-spinner-container--fullscreen" />;
  }
  if (error) return <p>Error: {error}</p>;
  if (!event) return <p>Event not found</p>;

  const maxTickets = getTicketsRemaining(event);
  const isSoldOut = maxTickets !== undefined && maxTickets <= 0;
  const totalPrice = event.ticketPrice * ticketQuantity;

  async function handlePayNow() {
    if (!event || isSoldOut || isRedirectingToPayment) return;

    setIsRedirectingToPayment(true);
    setTicketError(null);
    try {
      const session = await createEventTicketCheckoutSession(
        event._id,
        ticketQuantity,
        ticketComment,
      );
      if (!session.checkoutUrl) {
        setTicketError("Could not create payment session. Please try again.");
        return;
      }
      window.location.assign(session.checkoutUrl);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not start payment. Please try again.";
      setTicketError(message);
      setIsRedirectingToPayment(false);
    }
  }

  return (
    <div className="event-details-outer-container">
      {paymentNotice === "success" && (
        <div className="content-container">
          <div className="event-payment-message event-payment-message-success">
            <p>
              <strong>Thank you!</strong> Your payment was received and your
              tickets are confirmed. A confirmation email with your ticket PDF
              is on its way.
            </p>
          </div>
        </div>
      )}
      {paymentNotice === "cancelled" && (
        <div className="content-container event-payment-message event-payment-message-cancelled">
          <p>
            Payment was not completed. Your ticket reservation has been released
            — please try again.
          </p>
        </div>
      )}
      <div className="event-details-inner-container">
        <div className="event-detail-poster-container">
          {event.poster && (
            <img
              className="event-detail-poster"
              src={event.poster}
              alt={event.eventName}
            />
          )}
        </div>

        <div>
          <div className="event-meta-data">
            <h1 className="event-header">{event.eventName}</h1>
            <div className="event-details-date-location">
              <h2>Date & Location</h2>
              <div className="event-detail-flex-box">
                <FontAwesomeIcon
                  icon={faClock}
                  className="event-details-icon"
                />
                <p> {getFormattedDate(event.eventStartTime)} </p>
              </div>
              <div className="event-detail-flex-box">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="event-details-icon"
                />
                <p>{event.eventLocation}</p>
              </div>
            </div>

            <div className="event-details-description">
              <h2>About the event</h2>
              <div className="event-details-description-body">
                {linkifyPlainText(event.fullDescription ?? "")}
              </div>
            </div>
            <div className="event-details-ticket-info">
              <h2>Tickets</h2>
              {event.availableTicketCount !== undefined && (
                <p className="bold">Available tickets: {maxTickets} left</p>
              )}
              <p className="bold">Price: {event.ticketPrice} SEK</p>
              <label htmlFor="ticket-quantity" className="event-ticket-label">
                Number of tickets
              </label>
              <input
                id="ticket-quantity"
                className="event-ticket-input"
                type="number"
                min={1}
                max={maxTickets}
                step={1}
                value={ticketQuantity}
                onChange={(e) => {
                  const rawValue = Number(e.target.value);
                  if (Number.isNaN(rawValue)) return;
                  if (maxTickets === undefined) {
                    setTicketQuantity(rawValue);
                    return;
                  }
                  const next = Math.max(1, Math.min(rawValue, maxTickets));
                  setTicketQuantity(next);
                }}
                disabled={isSoldOut}
              />
              <label htmlFor="ticket-comment" className="event-ticket-label">
                Comment
              </label>
              <textarea
                id="ticket-comment"
                className="event-ticket-comment"
                placeholder="Dietary restrictions or other comment..."
                maxLength={EVENT_TICKET_COMMENT_MAX_LENGTH}
                rows={3}
                value={ticketComment}
                onChange={(e) => setTicketComment(e.target.value)}
                disabled={isSoldOut}
              />
              <p className="event-ticket-comment-count">
                {ticketComment.length}/{EVENT_TICKET_COMMENT_MAX_LENGTH}
              </p>
              <p className="bold">Total: {totalPrice} SEK</p>
              {ticketError ? <p>{ticketError}</p> : null}
              <button
                type="button"
                className={`btn ${isSoldOut ? "disabled-btn" : ""}`}
                disabled={isSoldOut || isRedirectingToPayment}
                onClick={handlePayNow}
              >
                {isRedirectingToPayment ? "Redirecting..." : "Buy tickets now"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsPage;
